import { ErrorOutline } from "@mui/icons-material";
import {
  Stack,
  Table,
  TableBody,
  TableCell,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useCallback, useMemo } from "react";
import type { WithCsvRowNumber } from "utils/csv-utils";

export interface CsvError {
  rowNumber: number;
  rowIdentifier?: string;
  errors: string[];
}

interface CsvErrorsTableProps {
  errors: WithCsvRowNumber<CsvError>[];
  showIdentifierColumn?: boolean;
  identifierColumnName?: string;
  rowsPerPageOptions?: number[];
}

export const CsvErrorsTable: React.FC<CsvErrorsTableProps> = ({
  errors,
  showIdentifierColumn = false,
  identifierColumnName = "Identifier",
  rowsPerPageOptions = [5, 10, 25],
}) => {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(rowsPerPageOptions[0]);

  const getUniqueIdentifierErrors = useCallback(() => {
    if (!showIdentifierColumn) return errors;

    // Group by identifier to remove duplicates
    const identifierToRowNumberMap: Map<string, WithCsvRowNumber<CsvError>[]> =
      errors.reduce((map, error) => {
        const key = error.value.rowIdentifier || "";
        const existing = map.get(key) || [];
        map.set(key, [...existing, error]);
        return map;
      }, new Map());

    // Convert back to array and sort by first row number
    return [...identifierToRowNumberMap.values()]
      .map((group) => group[0])
      .sort((a, b) => a.rowNumber - b.rowNumber);
  }, [errors, showIdentifierColumn]);

  const uniqueErrors = useMemo(
    () => getUniqueIdentifierErrors(),
    [getUniqueIdentifierErrors],
  );
  const paginatedErrors = uniqueErrors.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <>
      <Table>
        <TableBody>
          <TableRow>
            {showIdentifierColumn && (
              <TableCell component="th" scope="row" sx={{ fontWeight: "bold" }}>
                {identifierColumnName}
              </TableCell>
            )}
            <TableCell component="th" scope="row" sx={{ fontWeight: "bold" }}>
              CSV row
            </TableCell>
            <TableCell component="th" scope="row" sx={{ fontWeight: "bold" }}>
              Errors
            </TableCell>
          </TableRow>

          {paginatedErrors.map((error) => {
            const duplicateRows = showIdentifierColumn
              ? errors
                  .filter(
                    (e) => e.value.rowIdentifier === error.value.rowIdentifier,
                  )
                  .map((e) => e.rowNumber)
              : [error.rowNumber];

            return (
              <TableRow key={error.rowNumber}>
                {showIdentifierColumn && (
                  <TableCell>{error.value.rowIdentifier || ""}</TableCell>
                )}
                <TableCell>
                  {duplicateRows.map((row) => row - 1).join(", ")}
                </TableCell>
                <TableCell>
                  {duplicateRows.length > 1 && showIdentifierColumn ? (
                    <Typography
                      sx={{
                        backgroundColor: "rgba(211, 47, 47, 0.1)",
                        borderRadius: 1,
                        p: 1,
                        color: "text.primary",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <ErrorOutline color="error" fontSize="small" />
                      {`${identifierColumnName} is entered more than once.`}
                    </Typography>
                  ) : (
                    <Stack spacing={2}>
                      {error.value.errors.map((err, i) => (
                        <Typography
                          key={`${err}-${i}-${error.rowNumber}`}
                          sx={{
                            backgroundColor: "rgba(211, 47, 47, 0.1)",
                            borderRadius: 1,
                            p: 1,
                            color: "text.primary",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <ErrorOutline color="error" fontSize="small" />
                          {err}
                        </Typography>
                      ))}
                    </Stack>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={uniqueErrors.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={rowsPerPageOptions}
      />
    </>
  );
};
